import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import {
  getPsychometricTest,
  submitTestAnswers,
  type PsychometricTestDto,
} from '../../lib/psychometricTests';
import {
  Brain,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  LogOut,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/kinerja/psychometrics/$testId/take')({
  component: TakeTestPage,
});

function TakeTestPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <TakeTestContent />
    </ProtectedRoute>
  );
}

// Answer state: for scale/single_choice/multi_choice we store the option ID (string);
// for text we store the text string.
type AnswerValue = string;

function TakeTestContent() {
  const { testId } = Route.useParams() as { testId: string };
  const { user } = useAuth();

  const [test, setTest] = useState<PsychometricTestDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<{ totalScore: number; rawScores: Record<string, number>; completedAt: string } | null>(null);

  // Timer — single stable value; interval runs once
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Effect 1: fetch test
  useEffect(() => {
    let cancelled = false;
    getPsychometricTest(testId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) {
        setTest(res.data.test);
        if (res.data.test.estimatedTime) {
          setTimeLeft(res.data.test.estimatedTime * 60);
        }
      } else {
        setError('Test not found');
      }
    });
    return () => { cancelled = true; };
  }, [testId]);

  // Stable refs so doSubmit doesn't go stale
  const testRef = useRef<PsychometricTestDto | null>(null);
  const answersRef = useRef<Record<string, AnswerValue>>({});
  useEffect(() => { testRef.current = test; }, [test]);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  const doSubmit = useCallback(async () => {
    const currentTest = testRef.current;
    const currentAnswers = answersRef.current;
    if (!currentTest || !user) return;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setSubmitting(true);
    setSubmitError('');

    // Build answers in the format the backend expects:
    // - scale: selectedOptions: [stringValue]  (e.g. ["3"])
    // - single_choice: selectedOptions: [optionId]
    // - multi_choice: selectedOptions: [optionId]
    // - text: textAnswer: string
    const answersList = currentTest.questions.map((q) => {
      const qKey = q.id ?? q.text;
      const val = currentAnswers[qKey];
      if (q.type === 'text') {
        return { questionId: q.id ?? q.text, textAnswer: val ?? '' };
      }
      // scale, single_choice, multi_choice — val is either a raw scale number-string
      // or an option id string; both end up as selectedOptions[0]
      return {
        questionId: q.id ?? q.text,
        selectedOptions: val !== undefined ? [val] : [],
      };
    });

    const res = await submitTestAnswers(
      currentTest.id,
      { userId: user.id, companyId: user.companyId, answers: answersList },
    );
    setSubmitting(false);

    if (res.success && res.data) {
      const r = res.data.result as any;
      setResult({
        totalScore: typeof r.totalScore === 'number' ? r.totalScore : Number(r.totalScore) || 0,
        rawScores: r.rawScores ?? {},
        completedAt: r.completedAt ?? new Date().toISOString(),
      });
    } else {
      setSubmitError(res.error ?? 'Failed to submit test. Please try again.');
    }
  }, [user]);

  // Effect 2: timer tick
  useEffect(() => {
    if (timeLeft === null || result) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t === null || t <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // Effect 3: auto-submit on timeout
  useEffect(() => {
    if (timeLeft === 0 && !result) {
      doSubmit();
    }
  }, [timeLeft, result, doSubmit]);

  function setAnswer(questionKey: string, value: AnswerValue) {
    setAnswers((prev) => ({ ...prev, [questionKey]: value }));
  }

  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-xl mx-auto space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse w-64" />
        <div className="h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error || !test) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <p className="text-muted-foreground text-sm">{error || 'Test not found'}</p>
        <Link to={'/kinerja/psychometrics' as any}>
          <Button variant="link" className="mt-2">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Psychometrics
          </Button>
        </Link>
      </div>
    );
  }

  // ── No questions ─────────────────────────────────────────────────────────────
  if (test.questions.length === 0) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <p className="text-muted-foreground text-sm">This test has no questions yet.</p>
        <Link to={'/kinerja/psychometrics' as any}>
          <Button variant="link" className="mt-2">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </Button>
        </Link>
      </div>
    );
  }

  // ── Results ──────────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="max-w-xl mx-auto">
        <Card>
          <CardContent className="flex flex-col items-center py-10 text-center px-8">
            <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-lg font-semibold text-foreground">Assessment Complete</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-6">{test.name}</p>

            {/* Total score ring */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none"
                    strokeWidth="3"
                    strokeDasharray={`${result.totalScore} ${100 - result.totalScore}`}
                    strokeLinecap="round"
                    style={{ stroke: 'hsl(var(--primary))' }}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-foreground">
                  {Math.round(result.totalScore)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Score out of 100</p>
            </div>

            <Link to={'/kinerja/psychometrics' as any} className="mt-2">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back to Psychometrics
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Test in progress ──────────────────────────────────────────────────────────
  const questions = test.questions;
  const currentQ = questions[currentIdx];
  const qKey = currentQ.id ?? currentQ.text;
  const answered = answers[qKey] !== undefined && answers[qKey] !== '';
  const progress = Math.round(((currentIdx + 1) / questions.length) * 100);

  return (
    <div className="max-w-xl mx-auto space-y-4">
      {/* Exit link */}
      <Link to={'/kinerja/psychometrics' as any}>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          <LogOut className="w-4 h-4 mr-1.5" />
          Exit Test
        </Button>
      </Link>

      {/* Progress card */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{test.name}</span>
            </div>
            {timeLeft !== null && (
              <span
                className={cn(
                  'text-sm font-mono font-semibold flex items-center gap-1',
                  timeLeft < 60 ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                <Clock className="w-3.5 h-3.5" />
                {formatTime(timeLeft)}
              </span>
            )}
          </div>
          <Progress value={progress} className="h-1.5" />
          <p className="text-xs text-muted-foreground mt-1.5">
            Question {currentIdx + 1} of {questions.length}
          </p>
        </CardContent>
      </Card>

      {/* Question card */}
      <Card>
        <CardContent className="py-6 space-y-6">
          <p className="text-sm font-medium text-foreground leading-relaxed">
            {currentQ.text}
          </p>

          {/* Scale question — stores raw number as string (e.g. "3") */}
          {currentQ.type === 'scale' && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground px-0.5">
                <span>Strongly disagree</span>
                <span>Strongly agree</span>
              </div>
              <div className="flex justify-between gap-2">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAnswer(qKey, String(v))}
                    className={cn(
                      'flex-1 py-3 rounded-lg text-sm font-semibold border-2 transition-all',
                      answers[qKey] === String(v)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input text-muted-foreground hover:border-primary/50 hover:text-foreground',
                    )}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Single choice — stores option.id */}
          {currentQ.type === 'single_choice' && (
            <div className="space-y-2">
              {currentQ.options.map((opt) => (
                <button
                  key={opt.id ?? opt.text}
                  type="button"
                  onClick={() => setAnswer(qKey, opt.id ?? opt.text)}
                  className={cn(
                    'w-full text-left px-4 py-3 rounded-lg border-2 text-sm transition-all',
                    answers[qKey] === (opt.id ?? opt.text)
                      ? 'border-primary bg-accent text-primary font-medium'
                      : 'border-input text-foreground hover:border-primary/50',
                  )}
                >
                  {opt.text}
                </button>
              ))}
            </div>
          )}

          {/* Multi choice — stores option.id of selected option */}
          {currentQ.type === 'multi_choice' && (
            <div className="grid grid-cols-2 gap-3">
              {currentQ.options.map((opt) => (
                <button
                  key={opt.id ?? opt.text}
                  type="button"
                  onClick={() => setAnswer(qKey, opt.id ?? opt.text)}
                  className={cn(
                    'py-4 rounded-lg border-2 text-sm font-medium transition-all',
                    answers[qKey] === (opt.id ?? opt.text)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input text-foreground hover:border-primary/50',
                  )}
                >
                  {opt.text}
                </button>
              ))}
            </div>
          )}

          {/* Text question */}
          {currentQ.type === 'text' && (
            <Textarea
              placeholder="Type your answer here…"
              rows={4}
              value={answers[qKey] ?? ''}
              onChange={(e) => setAnswer(qKey, e.target.value)}
              className="resize-none text-sm"
            />
          )}

          {/* Submit error */}
          {submitError && (
            <div className="flex items-center gap-2 text-destructive text-xs bg-destructive/10 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {submitError}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
              disabled={currentIdx === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Previous
            </Button>

            {currentIdx < questions.length - 1 ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setCurrentIdx((i) => i + 1)}
                disabled={!answered}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={doSubmit}
                disabled={submitting || !answered}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {submitting ? 'Submitting…' : 'Submit'}
                <CheckCircle2 className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
